/**
 * What is true of this programme's team right now, above the two sections.
 *
 * The lead and module counts, then every reason the sections below may be read
 * only or refuse a write. Each is a separate line rather than one combined
 * banner: they have different causes and different fixes.
 *
 * @module components/teams/TeamStatus
 */

import React from "react";

import { recordVersionLabel } from "@/components/shared/revision-labels";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import { Alert, Badge, Divider, Heading, Icon } from "@/components/ui";
import type { WorkflowState } from "@/server/types/workflow";
import type { DevelopmentSetupResult } from "@/utils/development-setup";

export interface TeamStatusProps {
  /** Names the programme. Left out where the page header already does. */
  title?: string;
  workflowState: WorkflowState | undefined;
  saveRevision: number | string | undefined;
  leadCount: number;
  assignedModuleCount: number;
  moduleCount: number;
  loadError: Error | null;
  /** The team read is still out, so its counts are not yet answers. */
  loading?: boolean;
  feedback: { status: string; message: string };
  locked: boolean;
  /** Lead team management switched off for this tenant in Provider Settings. */
  leadManagementDisabled: boolean;
  /** The programme is still a Draft, so builders cannot be assigned yet. */
  builderAssignmentBlocked: boolean;
  developmentSetup: DevelopmentSetupResult | null;
}

export function TeamStatus({
  title = undefined,
  workflowState,
  saveRevision,
  leadCount,
  assignedModuleCount,
  moduleCount,
  loadError,
  loading = false,
  feedback,
  locked,
  leadManagementDisabled,
  builderAssignmentBlocked,
  developmentSetup,
}: TeamStatusProps): React.ReactElement {
  return (
    <>
      <Divider />
      <div className="flex flex-wrap items-start justify-between gap-3 py-4">
        <div>
          {title ? <Heading level={2}>{title}</Heading> : null}
          {/* Both come from the same read as the badges below, so an
              unmanaged programme whose read failed has neither: "Record version "
              with no number is worse than no line. */}
          <div className={`flex flex-wrap gap-2 ${title ? "mt-2" : ""}`.trim()}>
            {workflowState ? <WorkflowStateBadge state={workflowState} size="sm" /> : null}
            {saveRevision === undefined || saveRevision === "" ? null : (
              <Badge tone="neutral" variant="soft" size="sm">
                {recordVersionLabel(saveRevision)}
              </Badge>
            )}
          </div>
        </div>
        {/* Both counts come from the read, so until it answers they report an
            empty team rather than an unknown one — and `0 === 0` painted
            "0/0 modules assigned" success green while it was still out. */}
        {loadError || loading ? null : (
          <div className="flex flex-wrap gap-2">
            <Badge tone={leadCount ? "success" : "warning"} variant="soft" size="sm">
              <Icon name={leadCount ? "check-circle" : "warning-circle"} aria-hidden />
              {leadCount ? "Lead assigned" : "Lead required"}
            </Badge>
            <Badge
              tone={assignedModuleCount === moduleCount ? "success" : "warning"}
              variant="soft"
              size="sm"
            >
              <Icon name="squares-four" aria-hidden />
              {assignedModuleCount}/{moduleCount} modules assigned
            </Badge>
          </div>
        )}
      </div>
      <Divider />

      {loadError ? <Alert tone="warning">{loadError.message}</Alert> : null}
      {/* One region, mounted whatever the status. Every outcome of a write
          belongs in it, not just the one still running, and a live region
          inserted together with its text is commonly missed by NVDA and JAWS.
          The role sits here because `Alert` takes no aria passthrough. */}
      <div role="status">
        {feedback.status === "working" ? <Alert tone="info">{feedback.message}</Alert> : null}
        {feedback.status === "error" ? <Alert tone="warning">{feedback.message}</Alert> : null}
        {feedback.status === "success" ? <Alert tone="success">{feedback.message}</Alert> : null}
      </div>
      {locked ? (
        <Alert tone="info">
          This is a locked historical programme. Programme Team changes are read only.
        </Alert>
      ) : null}
      {leadManagementDisabled ? (
        <Alert tone="warning">
          Programme Team management for Programme Leads is disabled in Provider Settings.
        </Alert>
      ) : null}
      {builderAssignmentBlocked && developmentSetup ? (
        <Alert tone="warning" title="Programme setup must be completed first">
          <div className="mt-2 flex flex-wrap gap-2">
            {developmentSetup.requirements.map((requirement) => (
              <Badge
                key={requirement.key}
                tone={requirement.complete ? "success" : "neutral"}
                variant={requirement.complete ? "soft" : "outline"}
                size="sm"
              >
                <Icon name={requirement.complete ? "check" : "circle"} aria-hidden />
                {requirement.label}
              </Badge>
            ))}
          </div>
          <div className="mt-2 text-md">
            Complete the missing sections in Programme Development before assigning Module Builders.
          </div>
        </Alert>
      ) : null}
    </>
  );
}
